import { FeatureFlags } from '@dupe/common/constants'
import axios from 'axios'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { FASHION_DEPARTMENT_DEFAULT } from '~/src/components/onboarding/styleOptions'
import useIsFeatureOn from '~/src/hooks/useIsFeatureOn'
import { createSelectors } from '~/src/lib/createSelectors'
import { type EmailOwnerProvider, readEmailInUse } from '~/src/lib/emailInUse'
import logger from '~/src/lib/logger'
import { UpdateUserPayload } from '~/src/types'
import { idTokenForSend } from '~/src/lib/firebase/freshIdToken'

export enum OnboardingStep {
  WELCOME = 'WELCOME',
  SHOPPING_INTENT = 'SHOPPING_INTENT',
  STYLE = 'STYLE',
  QUALITY_VS_VALUE = 'QUALITY_VS_VALUE',
  DUPE_VALUE_PROP_ONE = 'DUPE_VALUE_PROP_ONE',
  DUPE_VALUE_PROP_TWO = 'DUPE_VALUE_PROP_TWO',
  JOB_TO_BE_DONE = 'JOB_TO_BE_DONE',
  INVITE_CODE = 'INVITE_CODE',
  PAYWALL = 'PAYWALL',
  COMPLETE = 'COMPLETE',
}

/**
 * Persist-schema version. Bumped to 2 when the brand-anchored StyleStep added a
 * new step + new `onboarding_data` fields — old localStorage state must not pin
 * a stale `steps` array (which would strand a mid-flow user) or miss the new
 * fields. See `migrate` + the custom `storage.getItem` below.
 */
const STORAGE_VERSION = 2

// Our frontend version of onboarding data
export interface OnboardingData {
  subscription?: {
    plan: string
    billingPeriod: string
    paymentStatus?: string
    liveMode?: boolean
  }
  stripeCustomerId?: string
  stripeSubscriptionId?: string
  // Onboarding data that will be saved to the database
  onboarding_data?: {
    shoppingIntent?: string | null
    qualityVsValue?: number | null
    jobToBeDone?: string | null
    isProfessional?: string | null
    // Brand-anchored StyleStep fields (ids from styleOptions.ts).
    category?: string | null
    homeStyle?: string[]
    fashionStyle?: string[]
    department?: string[]
    shoppingFor?: string[]
    materials?: string[]
  }
}

interface OnboardingState {
  currentStep: OnboardingStep
  steps: OnboardingStep[]
  data: OnboardingData
  completedSteps: OnboardingStep[]

  // Actions
  setCurrentStep: (step: OnboardingStep) => void
  nextStep: (nextStep?: OnboardingStep) => void
  previousStep: () => void
  setStepData: (data: Partial<OnboardingData>) => void
  markStepComplete: (step: OnboardingStep) => void
  isStepComplete: (step: OnboardingStep) => boolean
  resetOnboarding: () => void
  setPaymentStatus: (status: 'paid' | 'failed' | 'cancel') => void

  // API Actions
  subscribeNewsletter: (params: {
    authUser: {
      uid: string
      accessToken?: string
      getIdToken?: () => Promise<string>
    }
    tempUserId?: string
    email: string
    firstName?: string
    refetchUserData: () => Promise<any>
    /** DUP-1903 arm, tagged onto the Beehiiv subscriber so per-arm list quality
     *  is readable in the ESP. Optional: a caller outside the experiment (or one
     *  that hasn't resolved the flag) simply doesn't tag. */
    optinPlacement?: string
    /** The lawful basis this consent was taken under, as judged at decision time
     *  by `consentRegion.ts`: `active` where the shopper affirmatively ticked,
     *  `implied` for the US notice model. Beehiiv has no field for it; it is
     *  forwarded so the consent ledger can record it. Omitted ⇒ "not recorded",
     *  which is NOT the same as `implied`. */
    consentBasis?: 'active' | 'implied'
    /** Lowercase ISO-3166-1 alpha-2 of the IP country the basis was judged on. */
    jurisdiction?: string
    /** Which surface asked. */
    optinSurface?: 'onboarding_step' | 'signup_screen'
    /** Which onboarding flow the ask sat in, where it sat in one. */
    flow?: 'question' | 'brand'
    /** DUP-2460. True only from the phone sign-up's email ask, so that cohort's
     *  list performance is separable in Beehiiv. The route turns this into the
     *  tag; callers never name one. */
    phoneSignup?: boolean
  }) => Promise<{
    status: boolean
    error?: string
    /** Set when the address already belongs to a different Dupe account
     *  (HTTP 409). Nothing was written; `emailInUseProvider` says how that
     *  other account signs in, so the caller can say so. */
    code?: 'email_in_use'
    emailInUseProvider?: EmailOwnerProvider
  }>
  addTagToBeehiiv: (params: {
    authUser: {
      uid: string
      accessToken?: string
      getIdToken?: () => Promise<string>
    }
    tag: string
    subscriptionId: string
    refetchUserData: () => Promise<any>
  }) => Promise<{
    status: boolean
    error?: string
  }>

  // New onboarding data actions
  setShoppingIntent: (intent: string) => void
  setQualityVsValue: (value: number) => void
  setJobToBeDone: (job: string) => void
  setIsProfessional: (isProfessional: string) => void

  // StyleStep data actions
  setCategory: (category: string) => void
  setHomeStyle: (homeStyle: string[]) => void
  setFashionStyle: (fashionStyle: string[]) => void
  setDepartment: (department: string[]) => void
  setShoppingFor: (shoppingFor: string[]) => void
  setMaterials: (materials: string[]) => void
}

const INITIAL_DATA: OnboardingData = {
  subscription: {
    plan: 'free',
    billingPeriod: 'yearly',
  },
  onboarding_data: {
    shoppingIntent: null,
    qualityVsValue: null,
    jobToBeDone: null,
    isProfessional: null,
    category: null,
    homeStyle: [],
    fashionStyle: [],
    // Sensible department default from the study (see styleOptions.ts).
    // `shoppingFor` is left empty until a step actually collects it, so we
    // never persist an unasked value as if it were the shopper's choice.
    department: [...FASHION_DEPARTMENT_DEFAULT],
    shoppingFor: [],
    materials: [],
  },
}

const INITIAL_STATE: Omit<
  OnboardingState,
  | 'setCurrentStep'
  | 'nextStep'
  | 'previousStep'
  | 'setStepData'
  | 'markStepComplete'
  | 'isStepComplete'
  | 'resetOnboarding'
  | 'setProcessing'
  | 'setPaymentStatus'
  | 'subscribeNewsletter'
  | 'setShoppingIntent'
  | 'setQualityVsValue'
  | 'setJobToBeDone'
  | 'setIsProfessional'
  | 'setCategory'
  | 'setHomeStyle'
  | 'setFashionStyle'
  | 'setDepartment'
  | 'setShoppingFor'
  | 'setMaterials'
  | 'addTagToBeehiiv'
> = {
  currentStep: OnboardingStep.WELCOME,
  steps: [
    OnboardingStep.WELCOME,
    OnboardingStep.INVITE_CODE,
    OnboardingStep.SHOPPING_INTENT,
    OnboardingStep.STYLE,
    OnboardingStep.QUALITY_VS_VALUE,
    OnboardingStep.DUPE_VALUE_PROP_ONE,
    OnboardingStep.JOB_TO_BE_DONE,
    OnboardingStep.DUPE_VALUE_PROP_TWO,
    OnboardingStep.PAYWALL,
    OnboardingStep.COMPLETE,
  ],
  data: INITIAL_DATA,
  completedSteps: [],
}

// Create a single store instance with initialization check
const initializeStore = (preloadedState: Partial<OnboardingState> = {}) =>
  create<OnboardingState>()(
    persist(
      (set, get) => ({
        ...INITIAL_STATE,
        ...preloadedState,
        setCurrentStep: (step) => set({ currentStep: step }),
        nextStep: (nextStep?: OnboardingStep) => {
          const { currentStep, steps } = get()

          if (nextStep) {
            set({ currentStep: nextStep })
            return
          }

          const currentIndex = steps.indexOf(currentStep)
          if (currentIndex < steps.length - 1) {
            set({ currentStep: steps[currentIndex + 1] })
          }
        },
        previousStep: () => {
          const { currentStep, steps } = get()
          const currentIndex = steps.indexOf(currentStep)
          if (currentIndex > 0) {
            set({ currentStep: steps[currentIndex - 1] })
          }
        },
        setStepData: (data) =>
          set((state) => ({
            data: {
              ...state.data,
              ...data,
              // Ensure subscription is properly merged
              subscription: {
                ...(state.data.subscription || {}),
                ...(data.subscription || {}),
                // Ensure billing period is always defined
                billingPeriod:
                  data.subscription?.billingPeriod ||
                  state.data.subscription?.billingPeriod ||
                  'yearly',
                // Ensure plan is always defined
                plan:
                  data.subscription?.plan ||
                  state.data.subscription?.plan ||
                  'free',
              },
            },
          })),
        markStepComplete: (step) =>
          set((state) => ({
            ...state,
            completedSteps: [...new Set([...state.completedSteps, step])],
          })),
        isStepComplete: (step) => {
          const { completedSteps } = get()
          return completedSteps.includes(step)
        },
        resetOnboarding: () =>
          set({
            currentStep: OnboardingStep.WELCOME,
            data: INITIAL_DATA,
            completedSteps: [],
          }),
        setPaymentStatus: (status) =>
          set((state) => ({
            ...state,
            data: {
              ...state.data,
              subscription: {
                ...(state.data.subscription || {}),
                plan: state.data.subscription?.plan || 'free',
                billingPeriod:
                  state.data.subscription?.billingPeriod || 'yearly',
                paymentStatus: status,
              },
            },
          })),

        // New onboarding data actions
        setShoppingIntent: (intent: string) =>
          set((state) => ({
            data: {
              ...state.data,
              onboarding_data: {
                ...(state.data.onboarding_data || {}),
                shoppingIntent: intent,
              },
            },
          })),
        setQualityVsValue: (value: number) =>
          set((state) => ({
            data: {
              ...state.data,
              onboarding_data: {
                ...(state.data.onboarding_data || {}),
                qualityVsValue: value,
              },
            },
          })),
        setJobToBeDone: (job: string) =>
          set((state) => ({
            data: {
              ...state.data,
              onboarding_data: {
                ...(state.data.onboarding_data || {}),
                jobToBeDone: job,
              },
            },
          })),
        setIsProfessional: (isProfessional: string) =>
          set((state) => ({
            data: {
              ...state.data,
              onboarding_data: {
                ...(state.data.onboarding_data || {}),
                isProfessional: isProfessional,
              },
            },
          })),

        // StyleStep data actions
        setCategory: (category: string) =>
          set((state) => ({
            data: {
              ...state.data,
              onboarding_data: {
                ...(state.data.onboarding_data || {}),
                category,
              },
            },
          })),
        setHomeStyle: (homeStyle: string[]) =>
          set((state) => ({
            data: {
              ...state.data,
              onboarding_data: {
                ...(state.data.onboarding_data || {}),
                homeStyle,
              },
            },
          })),
        setFashionStyle: (fashionStyle: string[]) =>
          set((state) => ({
            data: {
              ...state.data,
              onboarding_data: {
                ...(state.data.onboarding_data || {}),
                fashionStyle,
              },
            },
          })),
        setDepartment: (department: string[]) =>
          set((state) => ({
            data: {
              ...state.data,
              onboarding_data: {
                ...(state.data.onboarding_data || {}),
                department,
              },
            },
          })),
        setShoppingFor: (shoppingFor: string[]) =>
          set((state) => ({
            data: {
              ...state.data,
              onboarding_data: {
                ...(state.data.onboarding_data || {}),
                shoppingFor,
              },
            },
          })),
        setMaterials: (materials: string[]) =>
          set((state) => ({
            data: {
              ...state.data,
              onboarding_data: {
                ...(state.data.onboarding_data || {}),
                materials,
              },
            },
          })),

        // Existing API Actions
        subscribeNewsletter: async ({
          authUser,
          tempUserId,
          email,
          firstName,
          refetchUserData,
          optinPlacement,
          consentBasis,
          jurisdiction,
          optinSurface,
          flow,
          phoneSignup,
        }) => {
          // The consent context rides along with the subscribe. None of it means
          // anything to Beehiiv — the route forwards it to the consent ledger,
          // which is the only place a lawful basis and a jurisdiction are
          // recorded. Each key is omitted rather than sent as undefined, so a
          // caller that genuinely doesn't know produces "not recorded" instead of
          // a guess.
          const valuesToStore = {
            tempUserIds: [tempUserId],
            email,
            firstName,
            ...(optinPlacement ? { optinPlacement } : {}),
            ...(consentBasis ? { consentBasis } : {}),
            ...(jurisdiction ? { jurisdiction } : {}),
            ...(optinSurface ? { optinSurface } : {}),
            ...(flow ? { flow } : {}),
            ...(phoneSignup ? { phoneSignup: true } : {}),
          } as UpdateUserPayload

          try {
            const token = await idTokenForSend(authUser)
            const res = await axios('/api/secure/newsletter-subscribe', {
              method: 'POST',
              data: valuesToStore,
              headers: {
                'x-authorization': token,
              },
            })

            // Check the BODY, not just the status. The endpoint reports the real
            // outcome in `subscribe`; trusting the status code alone made a
            // dropped subscribe look like a success.
            //
            // Surface the API's `reason` when it sends one. Defensive rather than
            // load-bearing: the endpoint now answers non-2xx on real failures, so
            // axios throws and the catch below handles those. This branch only
            // fires if something ever returns 200 with `subscribe: false` again.
            if (res?.status !== 200 || res?.data?.subscribe !== true) {
              return {
                status: false,
                error: res?.data?.reason || 'Error subscribing to newsletter',
              }
            }

            await refetchUserData()
            return { status: true }
          } catch (error) {
            const emailInUseProvider = readEmailInUse(error)
            if (emailInUseProvider) {
              return {
                status: false,
                error: 'Email already on another account',
                code: 'email_in_use',
                emailInUseProvider,
              }
            }
            logger.error('[subscribeNewsletter]', { error })
            return { status: false, error: 'Error subscribing to newsletter' }
          }
        },
        addTagToBeehiiv: async ({
          authUser,
          tag,
          subscriptionId,
          refetchUserData,
        }) => {
          try {
            const token = await idTokenForSend(authUser)
            const res = await axios('/api/secure/newsletter-tag', {
              method: 'POST',
              data: {
                tag,
                subscriptionId,
              },
              headers: {
                'x-authorization': token,
              },
            })

            if (res?.status !== 200) {
              return { status: false, error: 'Error adding tag to subscriber' }
            }

            await refetchUserData()

            return { status: true }
          } catch (error) {
            logger.error('[addTagToBeehiiv]', { error })
            return { status: false, error: 'Error adding tag to subscriber' }
          }
        },
      }),
      {
        name: 'onboarding-storage',
        version: STORAGE_VERSION,
        // Reconcile any older persisted shape: always adopt the current `steps`
        // array (so a returning mid-flow user gets the new StyleStep and can't
        // strand on a stale list) and backfill new `onboarding_data` defaults.
        migrate: (persisted) => {
          const prev = (persisted as Partial<OnboardingState>) || {}
          return {
            ...INITIAL_STATE,
            ...prev,
            steps: INITIAL_STATE.steps,
            data: {
              ...INITIAL_DATA,
              ...(prev.data || {}),
              onboarding_data: {
                ...INITIAL_DATA.onboarding_data,
                ...(prev.data?.onboarding_data || {}),
              },
            },
          } as OnboardingState
        },
        storage: {
          getItem: (name) => {
            if (typeof window === 'undefined') return null

            const str = localStorage.getItem(name)
            if (!str) return null

            try {
              const { state, version } = JSON.parse(str)
              return {
                // Preserve the stored version so persist runs `migrate` when it
                // predates the current STORAGE_VERSION. Blobs written before
                // versioning existed have no `version` — coerce to 0 so persist
                // still treats them as stale and runs `migrate`.
                version: typeof version === 'number' ? version : 0,
                state: {
                  ...INITIAL_STATE,
                  ...state,
                  // `steps` is static config, never restored from stale state —
                  // always use the current list so newly-added steps appear.
                  steps: INITIAL_STATE.steps,
                  // Ensure data structure (deep-merge onboarding_data so new
                  // fields/defaults exist for users persisted before the bump).
                  data: {
                    ...INITIAL_DATA,
                    ...(state.data || {}),
                    onboarding_data: {
                      ...INITIAL_DATA.onboarding_data,
                      ...(state.data?.onboarding_data || {}),
                    },
                  },
                },
              }
            } catch (_error) {
              return null
            }
          },
          setItem: (name, value) => {
            if (typeof window === 'undefined') return

            try {
              localStorage.setItem(name, JSON.stringify(value))
            } catch (error) {
              console.error('Error saving onboardingStore:', error)
            }
          },
          removeItem: (name) => {
            if (typeof window === 'undefined') return
            localStorage.removeItem(name)
          },
        },
      }
    )
  )

// Create a single store instance with initialization check
let store: ReturnType<typeof initializeStore> | undefined

const getStore = () => {
  if (typeof window === 'undefined') {
    // Always return a new store instance during SSR
    return initializeStore()
  }

  // Initialize store only once in client
  if (!store) {
    store = initializeStore()
  }
  return store
}

// Export the store hooks with selectors
export const useOnboardingStore = createSelectors(getStore())

// Specialized selector hooks
export const useOnboardingStep = () => {
  const currentStep = useOnboardingStore((state) => state.currentStep)
  const { featureOn: onboardingPaywallStepEnabled } = useIsFeatureOn(
    FeatureFlags.OnboardingPaywallStep
  )

  // Return COMPLETE if on paywall step but feature is disabled
  if (currentStep === OnboardingStep.PAYWALL && !onboardingPaywallStepEnabled) {
    return OnboardingStep.COMPLETE
  }

  return currentStep
}

export const useCompletedSteps = () =>
  useOnboardingStore((state) => state.completedSteps)

export const useOnboardingData = () =>
  useOnboardingStore((state) => state.data.onboarding_data)

export const useSubscriptionPlan = () =>
  useOnboardingStore((state) => state.data.subscription?.plan)
export const usePaymentStatus = () =>
  useOnboardingStore((state) => state.data.subscription?.paymentStatus)
export const useBillingPeriod = () =>
  useOnboardingStore((state) => state.data.subscription?.billingPeriod)

// Action groups for common operations
export const useOnboardingActions = () => {
  const store = useOnboardingStore()
  const { featureOn: onboardingPaywallStepEnabled } = useIsFeatureOn(
    FeatureFlags.OnboardingPaywallStep
  )

  return {
    nextStep: store.nextStep,
    previousStep: () => {
      const currentStep = store.currentStep
      const steps = store.steps
      const currentIndex = steps.indexOf(currentStep)

      if (currentIndex > 0) {
        const previousStep = steps[currentIndex - 1]
        const prePreviousStep = steps[currentIndex - 2]

        // If previous step would be paywall and feature is disabled, skip it
        if (
          previousStep === OnboardingStep.PAYWALL &&
          !onboardingPaywallStepEnabled
        ) {
          const existsInEnum =
            prePreviousStep &&
            Object.values(OnboardingStep).includes(prePreviousStep)
          if (existsInEnum) {
            store.setCurrentStep(prePreviousStep)
          } else {
            store.setCurrentStep(OnboardingStep.WELCOME)
          }
          return
        }

        if (previousStep) {
          store.setCurrentStep(previousStep)
        }
      }
    },
    setStepData: store.setStepData,
    markStepComplete: store.markStepComplete,
    resetOnboarding: store.resetOnboarding,
    subscribeNewsletter: store.subscribeNewsletter,
    setPaymentStatus: store.setPaymentStatus,
    setCurrentStep: store.setCurrentStep,
    setShoppingIntent: store.setShoppingIntent,
    setQualityVsValue: store.setQualityVsValue,
    setJobToBeDone: store.setJobToBeDone,
    setIsProfessional: store.setIsProfessional,
    setCategory: store.setCategory,
    setHomeStyle: store.setHomeStyle,
    setFashionStyle: store.setFashionStyle,
    setDepartment: store.setDepartment,
    setShoppingFor: store.setShoppingFor,
    setMaterials: store.setMaterials,
    addTagToBeehiiv: store.addTagToBeehiiv,
  }
}
